import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  FilterModel,
  IServerSideGetRowsParams,
  SortModelItem,
} from 'ag-grid-community';
import { useDispatch } from 'react-redux';

import {
  donorSchedulesApi,
  useLazyGetDonorSchedulesQuery as useLazyGetDonorSchedulesGqlQuery,
  useUpsertDonorSchedulesMutation as useUpsertDonorSchedulesGqlMutation,
} from '@/app/services/donorSchedules';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  donorSchedulesQueryOptions,
  fetchDonorSchedules,
  useUpsertDonorScheduleRestMutation,
} from '@/services/donor-scheduling';
import type { UpsertDonorScheduleRequest } from '@/services/donor-scheduling';
import { addGridQueryParams, buildFilterParam } from '@/utils/query';

const FLAG = '29169_donor_scheduling_to_rest';
const OR_FILTER_FLAG = 'DS_OR_filtering';

const REST_FIELD_MAPPINGS: Record<string, string> = {
  donor: 'donor.name',
  owner: 'owner.name',
  semenTypeValue: 'semenType',
  implantTypeValue: 'implantType',
};

export function useLazyDonorSchedulesQuery() {
  const isRest = useFeatureFlag(FLAG);
  const isOrFiltering = useFeatureFlag(OR_FILTER_FLAG);
  const queryClient = useQueryClient();
  const [gqlQuery] = useLazyGetDonorSchedulesGqlQuery();

  const restQuery = useCallback(
    (params: {
      request: IServerSideGetRowsParams['request'];
      fertilizationSiteIds?: string[];
      quickFilterText?: string;
      semenId?: string;
    }) => {
      const searchParams = new URLSearchParams();

      if (params.quickFilterText) {
        searchParams.set('search', params.quickFilterText);
      }
      if (params.semenId) {
        searchParams.set('semenId', `eq:${params.semenId}`);
      }
      if (params.fertilizationSiteIds?.length) {
        searchParams.set(
          'fertilizationSiteId',
          `in:${params.fertilizationSiteIds.join(',')}`,
        );
      }

      const filterModel = {
        ...((params.request.filterModel as FilterModel | null) ?? {}),
      };
      if (!filterModel.stateCode) {
        filterModel.stateCode = { filterType: 'set', values: [0] };
      }
      if (filterModel?.stateCode?.filterType === 'set') {
        filterModel.stateCode.values = (
          filterModel.stateCode.values as Array<string | number>
        )?.map((v) => (typeof v === 'number' ? v : v === 'Active' ? 0 : 1));
      }

      const sortModel = params.request.sortModel as SortModelItem[];
      const startRow = params.request.startRow ?? 0;
      const endRow = params.request.endRow ?? 500;

      if (isOrFiltering) {
        const gridParams = addGridQueryParams(
          searchParams,
          null,
          sortModel,
          startRow,
          endRow,
          REST_FIELD_MAPPINGS,
        );

        const filterParam = buildFilterParam(filterModel, REST_FIELD_MAPPINGS);
        if (filterParam) {
          gridParams.set('$filter', filterParam);
        }

        const promise = queryClient
          .fetchQuery(donorSchedulesQueryOptions(gridParams))
          .then((result) => ({
            donorSchedules: {
              items: result.items,
              hasMore: result.hasMore,
            },
          }));

        return {
          unwrap: () => promise,
        };
      }

      const gridParams = addGridQueryParams(
        searchParams,
        filterModel,
        sortModel,
        startRow,
        endRow,
        REST_FIELD_MAPPINGS,
      );

      const promise = fetchDonorSchedules(gridParams).then((result) => ({
        donorSchedules: {
          items: result.items,
          hasMore: startRow + result.items.length < result.totalCount,
        },
      }));

      return {
        unwrap: () => promise,
      };
    },
    [isOrFiltering, queryClient],
  );

  return { isRest, restQuery, gqlQuery };
}

type UpsertInput = {
  input: Record<string, unknown>;
  bulkUpdateIds?: string[];
};

export function useUpsertDonorSchedules() {
  const dispatch = useDispatch();
  const isRest = useFeatureFlag(FLAG);
  const queryClient = useQueryClient();
  const restMutation = useUpsertDonorScheduleRestMutation();
  const [gqlUpsert] = useUpsertDonorSchedulesGqlMutation();

  const mutate = useCallback(
    async (args: UpsertInput) => {
      if (isRest) {
        const {
          aspirationSite,
          csr,
          donor,
          owner,
          stateCode,
          fertilizationSite,
          textingAccount,
          scheduleMaster,
          drug,
          drug2,
          soSchedule,
          shipmentGroup,
          implantTypeValue,
          semen,
          sire,
          semenTypeValue,
          semenLocation,
          semenOwner,
          backupSemen,
          backupSire,
          billingAccount,
          partnerships,
          annotation,
          ...filteredInput
        } = args.input as Record<string, unknown>;

        const request = {
          ...filteredInput,
          annotationNoteText: (annotation as { noteText?: string })?.noteText,
        } as unknown as UpsertDonorScheduleRequest;

        const result = await restMutation.mutateAsync(request);
        dispatch(
          donorSchedulesApi.util.invalidateTags([
            'DonorSchedules',
            'AllTopRevisions',
            'Partnerships',
          ]),
        );
        await queryClient.invalidateQueries({
          queryKey: ['donor-scheduling', 'schedules'],
        });

        return {
          data: {
            upsertDonorSchedules: {
              code: result.code,
              message: result.message,
              success: result.success,
              donorSchedules: result.donorSchedules,
            },
          },
        };
      }

      return gqlUpsert(args);
    },
    [isRest, queryClient, restMutation, gqlUpsert, dispatch],
  );

  return [mutate, { isLoading: restMutation.isPending }] as const;
}
